import { Button, ButtonLevel, type ButtonProps, ButtonSize, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { cx } from '@vizit/theme';
import { formatDate } from '@vizit/utils';
import type * as React from 'react';

type IntegrationConnectionRowProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * Provider-side account identifier, used as the row title. Vendor accounts have no name, so this
   * is usually the only thing that tells two connections apart.
   *
   * @required
   * @type {string}
   */
  accountId: string;
  /**
   * Human-readable account name when the provider returns one. Left out for vendor accounts.
   *
   * @optional
   * @type {string}
   */
  accountName?: string | null;
  /**
   * Region the connection belongs to, e.g. `'Amazon US'`. Shown because an organization can hold
   * several connections in the same region.
   *
   * @optional
   * @type {string}
   */
  region?: string | null;
  /**
   * Timestamp when the connection was established. ISO string or Date.
   *
   * @required
   * @type {string | Date}
   */
  connectedAt: string | Date;
  /**
   * Email of the user who authorized the connection.
   *
   * @optional
   * @type {string}
   */
  authorizedBy?: string | null;
  /**
   * The label displayed on the disconnect button. The button is omitted when this is not set.
   *
   * @optional
   * @type {string}
   */
  disconnectLabel?: string;
  /**
   * Callback fired when the disconnect button is pressed.
   *
   * @optional
   * @type {ButtonProps['onPress']}
   */
  onDisconnect?: ButtonProps['onPress'];
  /**
   * Whether the disconnect request is in flight.
   *
   * @optional
   * @default false
   * @type {boolean}
   */
  isDisconnecting?: boolean;
  /**
   * Marks the row as the one the user has just added, so it is easy to spot in a longer list.
   *
   * @optional
   * @default false
   * @type {boolean}
   */
  isHighlighted?: boolean;
};

/**
 * IntegrationConnectionRow renders one connected account of an integration: which account it is,
 * where it lives, when it was connected and by whom, plus the action to disconnect it.
 *
 * @example
 * <IntegrationConnectionRow
 *   accountId="AB1CDE2FGHIJ3"
 *   region="Amazon US"
 *   connectedAt="2026-05-12T10:30:00.000Z"
 *   authorizedBy="buyer@acme.com"
 *   disconnectLabel="Disconnect"
 *   onDisconnect={() => confirmDisconnect()}
 * />
 */
function IntegrationConnectionRow(props: IntegrationConnectionRowProps) {
  const {
    accountId,
    accountName,
    region,
    connectedAt,
    authorizedBy,
    disconnectLabel,
    onDisconnect,
    isDisconnecting = false,
    isHighlighted = false,
    className,
    ...other
  } = props;

  const accountLabel = accountName || accountId;
  const connectedAtLabel = formatDate(typeof connectedAt === 'string' ? new Date(connectedAt) : connectedAt);
  const meta = [region, `Connected on ${connectedAtLabel}`, authorizedBy ? `by ${authorizedBy}` : null].filter(Boolean);

  return (
    <div
      className={cx('flex items-center justify-between gap-4 py-4', isHighlighted && 'bg-secondary px-4', className)}
      data-highlighted={isHighlighted || undefined}
      {...other}
    >
      <div className="flex flex-col gap-1 min-w-0">
        <Text size={TextSize.Sm} weight={TextWeight.Medium} color={TextColor.Primary}>
          {accountLabel}
        </Text>
        {accountName ? (
          <Text size={TextSize.Xs} color={TextColor.Tertiary}>
            {accountId}
          </Text>
        ) : null}
        <Text size={TextSize.Xs} color={TextColor.Tertiary} showEllipsis={false}>
          {meta.join(' · ')}
        </Text>
      </div>

      {disconnectLabel ? (
        <Button
          size={ButtonSize.Md}
          level={ButtonLevel.Secondary}
          onPress={onDisconnect}
          isLoading={isDisconnecting}
          className="shrink-0"
          aria-label={`${disconnectLabel} ${accountLabel}`}
        >
          {disconnectLabel}
        </Button>
      ) : null}
    </div>
  );
}

export { IntegrationConnectionRow };
export type { IntegrationConnectionRowProps };
